import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Inject, inject, Injectable } from '@angular/core';
import { catchError, firstValueFrom, mergeMap, Observable, switchMap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import { UserService } from '../providers/auth/user.service';
import { AuthentificationType, OidcSecurityService } from '../../oidc/oidc-security.service';
import { LoggerFactory } from '../providers/logger/logger-factory.service';
import { LoggerWrapper } from '../providers/logger/logger-wrapper.service';
import { NetworkProvider } from '../providers/network/network';
import { Events } from '../providers/events/events.provider';
import { OIDC_SERVICE_INJECTION_TOKEN } from '../../../main';

@Injectable({ providedIn: 'root' })
export class AuthInterceptor implements HttpInterceptor {
  private logger: LoggerWrapper = inject(LoggerFactory).getInstance(this);

  private readonly sensitiveRoutes = [
    `${environment.apis.API_CRT_IOT}/v2/donnees/send`,
    `${environment.apis.API_CRT_IOT}/v2/donnees/write`,
    `${environment.apis.API_CRT_IOT}/v2/programme-horaires/send`,
    `${environment.apis.API_CRT_IOT}/v2/loi-regulation/send`,
  ];
  constructor(
    @Inject(OIDC_SERVICE_INJECTION_TOKEN) private readonly oidcSecurityService: OidcSecurityService,
    private readonly userService: UserService,
    private readonly networkP: NetworkProvider,
    private readonly events: Events
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.startsWith(environment.apis.API_CRT_IOT)) {
      const headersWithCRT = this.addCrtHeaders(request.headers);
      request = request.clone({
        headers: headersWithCRT,
      });
    }

    // isCognitoUrl
    if (this.oidcSecurityService.isOidcRequest(request.url)) {
      return next.handle(request);
    }
    if (request.url.includes('X-Amz-Credential')) {
      return next.handle(request);
    }

    const authType = this.getAuthConfigId(request);

    return this.oidcSecurityService.getIdToken(authType).pipe(
      switchMap((idToken) => {
        if (!idToken && authType !== 'sensible') {
          return next.handle(request);
        }

        request = this.addAuthHeaders(request, idToken);

        return next.handle(request).pipe(
          catchError((httpError) => {
            this.logger.log(`[interceptorHttp:requestError] ${JSON.stringify(httpError)}`);
            this.networkP.addHistory('Requête en erreur', { req: request, httpError });

            if (httpError instanceof HttpErrorResponse && (httpError.status == 401 || httpError.status == 403)) {
              this.logger.log('[interceptorHttp:requestError] 401/403 from APIM refreshing tokens...');

              return this.oidcSecurityService.forceRefreshSession(authType).pipe(
                mergeMap((loginResponse) => {
                  this.networkP.addHistory('Rafrachissement du token OK', { idToken: loginResponse.idToken });
                  this.logger.log(
                    `[interceptorHttp:Token refresh] tokens refreshed successfully with token ${loginResponse.idToken}.`
                  );
                  const clonedReq = this.addAuthHeaders(request, loginResponse.idToken);
                  return next.handle(clonedReq);
                }),
                catchError((httpError) => {
                  console.log('refreshTokens catchError httpError', httpError);
                  if (
                    httpError instanceof HttpErrorResponse &&
                    (httpError.status == 400 || httpError.status == 401 || httpError.status == 403)
                  ) {
                    this.logger.log(
                      `[interceptorHttp:Token refresh] (Authentification ${authType}) Invalid grant, initiating login UI`
                    );

                    return this.loginViaUi(authType).pipe(
                      switchMap((idToken) => {
                        this.logger.log('[interceptorHttp:Token refresh] New tokens retrieved via UI login.');
                        const clonedReq = this.addAuthHeaders(request, idToken);
                        return next.handle(clonedReq);
                      }),
                      catchError((httpError) => {
                        this.logger.error(httpError, '[interceptorHttp:login via UI error]');
                        return throwError(() => httpError);
                      })
                    );
                  } else {
                    this.logger.error(httpError, '[interceptorHttp:Token refresh error]');
                    return throwError(() => httpError);
                  }
                })
              );
            } else {
              this.logger.error(httpError, '[interceptorHttp:Unhandled error]');
              return throwError(() => httpError);
            }
          })
        );
      })
    );
  }

  private addAuthHeaders(req: HttpRequest<any>, idToken: string) {
    return req.clone({
      headers: req.headers.set('Authorization', 'Bearer ' + idToken),
    });
  }

  private getAuthConfigId(request: HttpRequest<any>): AuthentificationType {
    if (this.sensitiveRoutes.some((route) => request.url.startsWith(route))) {
      return 'sensible';
    } else {
      return 'normal';
    }
  }

  private addCrtHeaders(headers: HttpHeaders): HttpHeaders {
    const matricule = this.userService.getMatricule();
    const login = this.userService.getLogin();
    const dkCodeRessource = this.userService.getDkCodeRessource();

    let resultHeaders = this.addHeader(headers, 'CRT-Matricule', matricule);
    resultHeaders = this.addHeader(resultHeaders, 'CRT-Login', login);
    resultHeaders = this.addHeader(resultHeaders, 'CRT-Dk-Code-Ressource', dkCodeRessource);
    resultHeaders = this.addHeader(resultHeaders, 'Provenance', 'MOBILITE');
    // Le header existant pour la partie CRT n'est pas normé (fr-FR, ...). On l'écrase pour mettre la valeur CRT
    resultHeaders = this.addHeader(resultHeaders, 'Accept-Language', 'fr');
    resultHeaders = this.addCrtDroitHeader(resultHeaders);

    return resultHeaders;
  }

  private addHeader(headers: HttpHeaders, headerKey: string, headerValue: string | string[]): HttpHeaders {
    if (headers === undefined || headers === null) {
      return headers;
    }

    if (headerValue !== undefined && headerValue !== null && headerValue.length > 0) {
      return headers.set(headerKey, headerValue);
    }

    return headers;
  }

  private addCrtDroitHeader(headers: HttpHeaders): HttpHeaders {
    const crtDroitHeaderValue = [];
    const isProfileLecture = this.userService.isProfilLecture();
    const isProfilLectureEcriture = this.userService.isProfilLectureEcriture();
    const isProfilIoT = this.userService.isProfilIoT();

    this.addDroit(crtDroitHeaderValue, isProfileLecture, '52', '2', '300');
    this.addDroit(crtDroitHeaderValue, isProfilLectureEcriture, '52', '2', '64', '300');
    this.addDroit(crtDroitHeaderValue, isProfilIoT, '30', '175', '300');
    return this.addHeader(headers, 'CRT-Droits', crtDroitHeaderValue.join(','));
  }

  private addDroit(crtDroitHeaderValue: string[], possedeDroit: boolean, ...droits: string[]): void {
    if (crtDroitHeaderValue === undefined || crtDroitHeaderValue === null) {
      return;
    }

    if (possedeDroit) {
      crtDroitHeaderValue.push(...droits);
    }
  }

  private loginViaUi(authType: 'normal' | 'sensible') {
    return new Observable<string>((observer) => {
      this.events.subscribe('authentification:loginSuccessful', async () => {
        this.logger.log('[interceptorHttp:loginViaUI] Login via UI successful.');
        const idToken = await firstValueFrom(this.oidcSecurityService.getIdToken(authType));

        observer.next(idToken);
        observer.complete();
      });

      this.oidcSecurityService.redirectToAdfsLoginPage(authType);
    });
  }
}
